import modelExtend from 'dva-model-extend';
import { t } from '@lingui/macro';
import deepmerge from 'deepmerge';
import { delay } from 'dva/saga';

import personModel, {
  isEmptyPatronymicFieldName,
  PersonFields,
} from './person';
import { PersonProps } from '../components/shared/Person/Person';
import { AppAction, Field } from '../declarations/widget-ums';
import getFullName from '../helpers/getFullName';
import { EMPTY_FIELD } from '../constants/emptyField';
import { hasErrors, validators } from '../validators';
import parseServerValidationError from '../helpers/parseServerValidationError';
import { addChildModalOpen } from './modals';
import addressModel from './address';
import { getValue } from '../constants/getFieldValues';
import documentModel from './document';
import fetchHelper from '../helpers/fetchHelper';
import {
  createField,
  getErrorPayload,
  getFIOPayload,
  transformPayloadToField,
} from '../helpers/fieldHelpers';
import { ADD_INFO } from '../components/children/Child/helpers/renderSecondParentSuggestion';
import {
  CHANGED_FIO_FIELD_NAME,
  EMPTY_PATRONYMIC_LABEL,
  FIO_FIELDS,
  IS_EMPTY_PATRONYMIC_FIELD_NAME,
  ORGANIZATION_FIELD_NAME,
  PATRONYMIC_FIELD_NAME,
  PERSON_FIELD_NAME,
} from '../constants/fieldNames';
import cardModel, { cardInitialState, CardProps } from './card';
import { DOCUMENT_FIELD, documentInitialState } from '../constants/document';
import {
  getEducationIsFoundInCatalog,
  getPolyclinicIsFoundInCatalog,
  childShouldHavePassport,
  getChildCitizenship,
  isSecondParentDivorced,
} from '../selectors/child';
import { DEFAULT_FETCH_OPTIONS } from './auth';
import {
  shouldBeGettingEducation,
  isAdult,
  isDateInvalid,
} from '../helpers/dateHelpers';
import { CHILD_ADDRESS, CONFIRMING_DOCUMENTS } from '../constants/child';
import { i18n } from '../helpers/i18n';
import { validateSecondParentMarriageDate } from '../validators/child';

import { organizationError, sendStats } from '../helpers/stats';
import { getNewChildId, getId } from '../helpers/getIds';
import {
  initialChildState,
  initialOrganizationState,
  initialSecondParentState,
  Organization,
} from '../helpers/initialState';

import { DELAY_TIME } from '../constants/delayTime';
import { checkBtiCode, SPECIAL_VALUE } from '../helpers/address';
import { sendCMATechError } from '../helpers/CMAstats';

export const CHILD = 'child';

export const LOADING = [
  {
    value: SPECIAL_VALUE.loading,
  },
];

export const NOT_FOUND_EDUCATION = [
  {
    value: SPECIAL_VALUE.notFound,
    text: i18n._(t`Не найдено в списке организаций`),
  },
  {
    value: SPECIAL_VALUE.switchToManual,
    text: i18n._(t`Укажите вручную`),
  },
];

export const NOT_FOUND_POLYCLINIC = [
  {
    value: SPECIAL_VALUE.notFound,
    text: i18n._(t`Не найдено в списке учреждений`),
  },
  {
    value: SPECIAL_VALUE.switchToManual,
    text: i18n._(t`Укажите вручную`),
  },
];

export const ERROR = [
  {
    value: SPECIAL_VALUE.error,
    text: i18n._(t`По техническим причинам не удалось выполнить поиск`),
  },
  { value: SPECIAL_VALUE.retry, text: i18n._(t`Повторить поиск`) },
];

interface BasicSecondParent
  extends Pick<
    PersonFields,
    'name' | 'surname' | 'patronymic' | 'birthdate' | 'isPatronymicAbsent'
  > {
  id: string;
}

export interface PaternitySecondParent extends BasicSecondParent {
  status: Field<'paternity'>;
  paternityRegisterDate: Field<string>;
  paternityRegisterNumber: Field<string>;
}

export interface DivorcedSecondParent extends BasicSecondParent {
  status: Field<'divorced'>;
  dateOfMarriage: Field<string>;
  dateOfDivorce: Field<string>;
  divorceDocIssuer: Field<string>;
}

export interface DeadSecondParent extends BasicSecondParent {
  status: Field<'widowed'>;
  deathdate: Field<string>;
  proofOfDeath: Field<string>;
  deathDocIssuer: Field<string>;
}

export interface SingleSecondParent extends BasicSecondParent {
  status: Field<'single'>;
}

export type SecondParent = BasicSecondParent &
  (
    | PaternitySecondParent
    | DivorcedSecondParent
    | DeadSecondParent
    | SingleSecondParent
    | { status: Field<string> }
  );

export interface ChildProps extends PersonProps {
  id: string | number;
  selectedParent: Field<string>;
  bornInNotCurrentMarriage: Field<string>;
  secondParent: SecondParent;
  addressMatchWithApplicant: Field<CHILD_ADDRESS | null>;
  bornInCurrentMarriage: Field<string>;
  confirmingDocs: Field<string>;
  isStudying: boolean;
  showSecondParentContent: boolean;
  education: Organization;
  polyclinic: Organization;
  cardStatus: CardProps;
  hidden?: boolean;
  isRegisteredInMoscow: boolean;
}

export interface ChildFields extends PersonFields {
  education: Organization;
  secondParent: SecondParent;
  polyclinic: Organization;
  confirmingDocs: Field<CONFIRMING_DOCUMENTS | ''>;
  bornInNotCurrentMarriage: Field;
  isStudying: boolean | null;
  bornInCurrentMarriage: Field;
  id: string;
  fullName: string;
  selectedParent: Field;
  addressMatchWithApplicant: Field<CHILD_ADDRESS | null>;
  cardStatus: CardProps;
  showSecondParentContent: boolean;
  isRegisteredInMoscow: boolean;
}

interface ChildValidationResultProps {
  child: { value?: { error: string | boolean } };
}

interface ChildState {
  child: ChildProps;
}

const childModel = modelExtend(personModel, addressModel, cardModel, {
  namespace: CHILD,
  state: initialChildState,
  reducers: {
    reset() {
      return Object.assign({}, childModel.state, {
        secondParent: { ...initialSecondParentState, id: getId() },
      });
    },
    edit(state, { payload }) {
      return deepmerge<ChildState, ChildState>(childModel.state, payload);
    },
    'secondParent/change'(state, { payload }) {
      const [key] = Object.keys(payload);
      const newPayload = FIO_FIELDS.includes(key)
        ? getFIOPayload(payload)
        : payload;

      state.secondParent = Object.entries(newPayload).reduce(
        (nextState, [key, value]) => {
          nextState[key] = value;

          if (isEmptyPatronymicFieldName(key)) {
            const field = IS_EMPTY_PATRONYMIC_FIELD_NAME[key];
            nextState[field] = { ...state[field], value: true };
            nextState[PATRONYMIC_FIELD_NAME[key]] = createField(
              i18n._(t`Нет отчества`)
            );
          }
          if (
            [
              PERSON_FIELD_NAME.patronymic,
              CHANGED_FIO_FIELD_NAME.changedPatronimic,
            ].includes(key)
          ) {
            const field =
              IS_EMPTY_PATRONYMIC_FIELD_NAME[EMPTY_PATRONYMIC_LABEL[key]];
            const isPatronymicEmpty = {
              [field]: { ...nextState[field], value: false },
            };
            return {
              ...nextState,
              ...isPatronymicEmpty,
              ...getFIOPayload(payload),
            };
          }
          return nextState;
        },
        Object.assign({}, state.secondParent)
      );

      return state;
    },
    'secondParent/reset/status'(state, { payload }) {
      state.secondParent = Object.entries(payload).reduce(
        (nextState, [key, value]) => {
          nextState[key] = value;
          return nextState;
        },
        Object.assign(
          {},
          { ...initialSecondParentState, id: state.secondParent.id }
        )
      );
      state.confirmingDocs = { ...EMPTY_FIELD };
      state.selectedParent = { ...EMPTY_FIELD };
      state.showSecondParentContent = false;
      return state;
    },
    'secondParent/select'(state, { payload }) {
      const { value, ...secondParentProps } = payload;
      const isNewInfo = value === ADD_INFO;
      const { status } = state.secondParent;
      const resetedState = {
        ...initialSecondParentState,
        id: getId(),
        status,
      };
      state.secondParent = isNewInfo
        ? resetedState
        : Object.assign(state.secondParent, {
            ...secondParentProps,
          });
      state.selectedParent = { value };

      return state;
    },
    'bornInNotCurrentMarriage/reset'(state, { payload }) {
      state.bornInNotCurrentMarriage = { ...EMPTY_FIELD };
      state.secondParent = {
        ...initialSecondParentState,
        id: state.secondParent.id,
      };
      state = Object.entries(payload).reduce((nextState, [key, value]) => {
        nextState[key] = value;
        return nextState;
      }, state);
      return state;
    },
    'validation/failed'(state, { payload }) {
      const { child, childValidationResult } = payload;
      const getSelectedParentError = (secondParent) => {
        if (secondParent) {
          return [
            secondParent.name.error,
            secondParent.surname.error,
            secondParent.patronymic.error,
          ].every(Boolean);
        }
        return false;
      };
      state = deepmerge(child, childValidationResult);
      state.selectedParent.error = getSelectedParentError(
        childValidationResult.secondParent
      );
      return state;
    },
    'document/set/values'(state, { payload, documentField }) {
      const field = DOCUMENT_FIELD[documentField];
      const errorPayload = getErrorPayload(payload);

      if (documentField === DOCUMENT_FIELD.dul) {
        return documentModel.reducers['document/set/values'](state, {
          payload,
          documentField,
        });
      }

      if (payload.type) {
        Object.assign(
          state[field],
          payload.type ? documentInitialState : {},
          transformPayloadToField('value', payload, state[field])
        );
        return state;
      }

      Object.assign(
        state[field],
        transformPayloadToField('value', payload, state[field])
      );
      Object.assign(
        state[field],
        transformPayloadToField('error', errorPayload, state[field])
      );
      return state;
    },
    'isStudying/change'(state, { payload }) {
      const { isStudying } = payload;
      if (!isStudying) {
        state.education = { ...initialOrganizationState };
      }
      state.isStudying = isStudying;
      return state;
    },
    'education/change'(state, { payload }) {
      state.education = Object.entries(payload).reduce(
        (nextState, [key, value]) => {
          nextState[key] = value;
          if (key === ORGANIZATION_FIELD_NAME.isFoundInCatalog) {
            state.education.institutionName = { ...EMPTY_FIELD };
          }
          return nextState;
        },
        state.education
      );
      state.isStudying = true;
      if (state.education.institutionName.value.length < 3) {
        state.education.suggestions = [];
      }
      if (state.polyclinic.institutionName.error) {
        state.polyclinic.institutionName.error = false;
      }
      return state;
    },
    'education/set'(state, { payload: value }) {
      const newValue = {
        value,
        error: false,
      };
      state.education.institutionName = newValue;
      state.isStudying = true;
      return state;
    },
    'education/suggestions/update'(state, { payload }) {
      if (payload === LOADING) {
        state.education.error = false;
      }
      if (payload === ERROR) {
        state.education.error = true;
      }
      state.education.suggestions = payload;
      return state;
    },
    'education/suggestions/set'(state, { payload }) {
      state.education.institutionName = payload;
      state.education.isFoundInCatalog = true;
      state.isStudying = true;
      return state;
    },
    'education/institutionName/setError'(state, { payload }) {
      state.education.institutionName.error = payload;
      return state;
    },
    'polyclinic/change'(state, { payload }) {
      state.polyclinic = Object.entries(payload).reduce(
        (nextState, [key, value]) => {
          nextState[key] = value;
          if (key === ORGANIZATION_FIELD_NAME.isFoundInCatalog) {
            state.polyclinic.institutionName = EMPTY_FIELD;
          }
          return nextState;
        },
        state.polyclinic
      );
      if (state.polyclinic.institutionName.value.length < 3) {
        state.polyclinic.suggestions = [];
      }
      if (
        !shouldBeGettingEducation(getValue(state.birthdate)) &&
        state.education.institutionName.error
      ) {
        state.education.institutionName.error = false;
      }
      return state;
    },
    'polyclinic/set'(state, { payload: value }) {
      const newValue = {
        value,
        error: false,
      };
      state.polyclinic.institutionName = newValue;
      return state;
    },
    'polyclinic/suggestions/update'(state, { payload }) {
      if (payload === LOADING) {
        state.polyclinic.error = false;
      }
      if (payload === ERROR) {
        state.polyclinic.error = true;
      }
      state.polyclinic.suggestions = payload;
      return state;
    },
    'polyclinic/suggestions/set'(state, { payload }) {
      state.polyclinic.institutionName = payload;
      state.polyclinic.isFoundInCatalog = true;
      return state;
    },
    'polyclinic/institutionName/setError'(state, { payload }) {
      state.polyclinic.institutionName.error = payload;
      return state;
    },
    'secondParentContent/show'(state, { payload }) {
      state.showSecondParentContent = payload;
      return state;
    },
    'reset/error/birthdate'(state) {
      return deepmerge(state, { birthdate: { error: false } });
    },
    'secondParent/set/error/relativeFields'(state, { payload }) {
      const { fields, error } = payload;
      fields.forEach((field) => {
        state.secondParent[field].error = error;
      });
      return state;
    },
    'address/toggle/isRegisteredInMoscow'(
      state: ChildProps,
      { payload }: AppAction<boolean>
    ) {
      state.isRegisteredInMoscow = payload;
      return state;
    },
    'confirmingDocs/reset'(state: ChildProps) {
      state.confirmingDocs = { ...EMPTY_FIELD };
      state.education = { ...initialOrganizationState };
      state.polyclinic = { ...initialOrganizationState };
      return state;
    },
  },
  effects: {
    *save({}, { select, put }) {
      const state = yield select((state) => state);
      const validationResult = parseServerValidationError(
        validators.validateChild(state)
      );
      const childValidationResult = (validationResult as ChildValidationResultProps)
        .child;

      const child = yield select((state) => state.child);
      const { id: childId, secondParent } = child;
      const id = childId || getNewChildId();
      const fullName = getFullName(child);
      const secondParentId = secondParent.id || getId();

      const value = getFullName(secondParent);
      const validatedChild = deepmerge(child, childValidationResult);

      yield put({
        type: 'children/save',
        payload: {
          ...validatedChild,
          secondParent: {
            ...validatedChild['secondParent'],
            id: secondParentId,
          },
          id,
          fullName,
          hidden: false,
          selectedParent: { value },
          showSecondParentContent: false,
          cardStatus: cardInitialState,
        },
      });
      yield put({
        type: 'notHiddenChildren/add',
        payload: id,
      });
      yield put({ type: 'children/validate' });
    },
    *validate({ payload }, { select, put }) {
      const state = yield select((state) => state);
      const validationResult = parseServerValidationError(
        validators.validateChild(state)
      );

      const childValidationResult = (validationResult as ChildValidationResultProps)
        .child;

      const childHasErrors = hasErrors(childValidationResult);
      const child = state.child;
      if (childHasErrors) {
        yield put({
          type: 'validation/failed',
          payload: { child, childValidationResult },
        });
        yield put({
          type: 'form/scrollToError',
          payload: '.ChildModal',
        });
      } else {
        yield put({
          type: 'save',
          payload,
        });
        yield put(addChildModalOpen(false));
      }
    },
    *'education/change'({ payload }, { select, put }) {
      const { institutionName } = payload;
      const isFoundInCatalog = yield select(getEducationIsFoundInCatalog);
      if (!institutionName || !isFoundInCatalog) return;

      const value = getValue(institutionName);
      const trimmedValue = value.trim();
      if (trimmedValue.length < 3) return;
      yield put({ type: 'education/fetch', payload: trimmedValue });
    },
    *'education/retry'({}, { select, put }) {
      const payload = yield select(
        (state) => state.child.education.institutionName.value
      );
      yield put({ type: 'education/fetch', payload });
    },
    'education/fetch': [
      function* ({ payload }, { call, put }) {
        yield put({ type: 'education/suggestions/update', payload: LOADING });
        yield delay(DELAY_TIME);
        try {
          const response = yield call(
            fetchHelper,
            `/api/mss-facade/v1/search-in-registry/institution?name=${encodeURIComponent(
              payload
            )}`,
            DEFAULT_FETCH_OPTIONS
          );
          // поиск на стенде был отключен, пока переводим пользователя в
          // режим ручного ввода организации
          if (response.status === 500) {
            sendCMATechError('step');
            sendStats(organizationError('failed_to_search_school'));
            yield put({
              type: 'education/suggestions/update',
              payload: NOT_FOUND_EDUCATION,
            });
            return;
          }
          const { data } = yield call([response, 'json']);
          const { organizations } = data;
          if (!organizations.length) {
            yield put({
              type: 'education/suggestions/update',
              payload: NOT_FOUND_EDUCATION,
            });
            return;
          }
          const suggestions = organizations.map(({ name, id }) => ({
            value: id,
            text: name,
          }));

          yield put({
            type: 'education/suggestions/update',
            payload: suggestions,
          });
        } catch (e) {
          sendCMATechError('step');
          sendStats(organizationError('failed_to_search_school'));
          yield put({ type: 'education/suggestions/update', payload: ERROR });
        }
      },
      { type: 'takeLatest' },
    ],
    *'polyclinic/change'({ payload }, { select, put }) {
      const { institutionName } = payload;
      const isFoundInCatalog = yield select(getPolyclinicIsFoundInCatalog);
      if (!institutionName || !isFoundInCatalog) return;

      const value = getValue(institutionName);
      const trimmedValue = value.trim();
      if (trimmedValue.length < 3) return;
      yield put({ type: 'polyclinic/fetch', payload: trimmedValue });
    },
    *'polyclinic/retry'({}, { select, put }) {
      const payload = yield select(
        (state) => state.child.polyclinic.institutionName.value
      );
      yield put({ type: 'polyclinic/fetch', payload });
    },
    'polyclinic/fetch': [
      function* ({ payload }, { call, put }) {
        yield put({ type: 'polyclinic/suggestions/update', payload: LOADING });
        yield delay(DELAY_TIME);
        try {
          const response = yield call(
            fetchHelper,
            `/aisearch/facility/clinics/v1/suggest/?type=40&district=&position=&station=&query=${encodeURIComponent(
              payload
            )}`,
            DEFAULT_FETCH_OPTIONS
          );
          if (response.status === 500) {
            sendCMATechError('step');
            sendStats(organizationError('failed_to_search_hospital'));
            yield put({
              type: 'polyclinic/suggestions/update',
              payload: ERROR,
            });
            return;
          }
          const organizations = yield call([response, 'json']);

          if (!organizations.length) {
            yield put({
              type: 'polyclinic/suggestions/update',
              payload: NOT_FOUND_POLYCLINIC,
            });
            return;
          }
          const suggestions = organizations.map(({ name, url }) => ({
            value: url,
            text: name,
          }));

          yield put({
            type: 'polyclinic/suggestions/update',
            payload: suggestions,
          });
        } catch (e) {
          sendCMATechError('step');
          sendStats(organizationError('failed_to_search_hospital'));
          yield put({ type: 'polyclinic/suggestions/update', payload: ERROR });
        }
      },
      { type: 'takeLatest' },
    ],
    *'check/birthdate'({ payload }, { select, put }) {
      const shouldHavePassport = yield select(childShouldHavePassport);

      if (!shouldHavePassport) {
        const payload = yield select(getChildCitizenship);
        yield put({ type: 'dul/reset', payload });
      }
      const isAdultChild = !isDateInvalid(payload) && isAdult(payload);

      if (isAdultChild) {
        yield put({ type: 'confirmingDocs/reset' });
      }
    },
    *'secondParent/validateMarriageDate'({}, { select, put }) {
      const isDivorcedSecondParent = yield select(isSecondParentDivorced);
      if (isDivorcedSecondParent) {
        const error = yield select(validateSecondParentMarriageDate);
        yield put({
          type: `secondParent/set/error/relativeFields`,
          payload: {
            fields: ['dateOfMarriage'],
            error,
          },
        });
      }
    },
  },
});

export default childModel;
